// App.js (User app) — URL routing via React Navigation linking + SAFE auth gating + keeps Router context on web
import React, { useEffect, useMemo, useState } from 'react';
import { View, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

// ✅ Keep Router context on WEB only (prevents crashes if any screen uses Link/useParams/etc)
import { BrowserRouter as Router } from 'react-router-dom';

// Import your components (UNCHANGED)
import Homepage from './Homepage';
import CreateSubmissionPage from './CreateSubmissionPage';
import UserRecapPage from './UserRecapPage';
import UserCollabDashboard from './UserCollabDashboard';
import CollabCampaignDetailsPage from './CollabCampaignDetailsPage';
import UserGetAssignQRPage from './UserGetAssignQRPage';
import UserGetQRSearch from './UserGetQRSearch';
import QRCodeScannerPage from './QRCodeScannerPage';
import BrandURLLandingPage from './BrandURLLandingPage';
import MediaUpload from './MediaUpload';
import SignUpPage from './SignUpPage';
import LogInPage from './LogInPage';
import CollabCalendar from './CollabCalendar';
import BrandListings from './BrandListings';
import InfluencerInviteLink from './InfluencerInviteLink';
import ManualVisitRedeem from './ManualVisitRedeem';
import VenueDirectory from './VenueDirectory';
import UserActivityFeed from './UserActivityFeed';
import UnclaimedSearch from './UnclaimedSearch';
import SimilarVenues from './SimilarVenues';
import MyFeed from './MyFeed';
import Feed from './Feed';
import PreReleaseListings from './PreReleaseListings';


const Stack = createStackNavigator();

// ✅ Your real HTML login URL
const SIGNIN_URL = 'https://collabbay.com/SignIn';

/**
 * ✅ Deep linking config (admin-style)
 * Makes URLs map to screens while keeping navigation.navigate() working.
 */
const linking = {
  prefixes: [
    'https://collabbay.com',
    'https://www.collabbay.com',
    'http://localhost:19006',
    'http://127.0.0.1:19006',
  ],
  config: {
    screens: {
      // Root
      Homepage: '',

      // Public deep links you already had
      SignUpPage: 'SignUp/:Referrer?',
      BrandURLLandingPage: 'collab/:CollabID',
      CreateSubmissionPage: 'collaboffer/:ListingID',
      InfluencerInviteLink: 'invite/:InvitationID',

      // Auth (keep a route, but NOT /SignIn so it doesn’t clash with your real HTML page)
      LogInPage: 'rn-login',

      // Private (now also get URLs)
      UserRecapPage: 'UserRecapPage',
      UserCollabDashboard: 'UserCollabDashboard',
      CollabCampaignDetailsPage: 'CollabCampaignDetailsPage/:CollabID?',
      UserGetAssignQRPage: 'UserGetAssignQRPage/:CollabID?',
      UserGetQRSearch: 'UserGetQRSearch',
      QRCodeScannerPage: 'QRCodeScannerPage',
      MediaUpload: 'MediaUpload/:CollabID?',
      CollabCalendar: 'CollabCalendar',
      BrandListings: 'BrandListings',
      ManualVisitRedeem: 'ManualVisitRedeem/:CollabID?',
      VenueDirectory: 'VenueDirectory',
      UserActivityFeed: 'UserActivityFeed',
      UnclaimedSearch: 'UnclaimedSearch',
      SimilarVenues: 'SimilarVenues/:ListingID?',
      MyFeed: 'MyFeed',
      Feed: 'Feed',
      PreReleaseListings: 'PreReleaseListings',

    },
  },
};

// Small helper: treat these as public entry routes (don’t block initial render waiting for session)
const isPublicEntryPath = () => {
  if (Platform.OS !== 'web') return false;
  const p = window?.location?.pathname || '/';
  return (
    /^\/collaboffer\/[^/]+/i.test(p) ||
    /^\/collab\/[^/]+/i.test(p) ||
    /^\/invite\/[^/]+/i.test(p) ||
    /^\/SignUp(\/.*)?$/i.test(p) ||
    /^\/SignIn\/?$/i.test(p) // ✅ your real HTML login path
  );
};

const FullscreenLoader = () => (
  <View style={styles.loadingContainer}>
    <ActivityIndicator size="large" color="#000" />
  </View>
);

/**
 * ✅ WebLoginRedirect wrapper:
 * - If anything navigates to "LogInPage" inside the app on WEB, we redirect to your real HTML /SignIn
 * - On native (if ever used), it renders the existing LogInPage component
 */
const WebLoginRedirect = (props) => {
  useEffect(() => {
    if (Platform.OS === 'web') {
      window.location.href = SIGNIN_URL;
    }
  }, []);

  if (Platform.OS === 'web') return <FullscreenLoader />;
  return <LogInPage {...props} />;
};

/**
 * ✅ AuthGate:
 * - Keeps ALL screens registered (prevents linking → missing-screen crash)
 * - If not logged in:
 *    - WEB: hard redirect to https://collabbay.com/SignIn (your real HTML page)
 *    - native: navigation.replace('LogInPage')
 */
const AuthGate = ({ username, loadingSession, navigation, children }) => {
  useEffect(() => {
    if (!loadingSession && !username) {
      if (Platform.OS === 'web') {
        window.location.href = SIGNIN_URL;
        return;
      }
      navigation.replace('LogInPage');
    }
  }, [loadingSession, username, navigation]);

  if (loadingSession) return <FullscreenLoader />;
  if (!username) return <FullscreenLoader />; // brief moment while redirect/replace happens
  return children;
};

const AppShell = () => {
  const [username, setUsername] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  const shouldBlockInitialRender = useMemo(() => {
    // If you open a PUBLIC entry URL, don’t block rendering waiting for session
    // (mirrors your previous Router behaviour where those pages opened instantly)
    return !isPublicEntryPath();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchSessionData = async () => {
      // If we’re not blocking, allow UI to paint immediately
      if (!shouldBlockInitialRender) setLoadingSession(false);

      try {
        const response = await fetch(
          'https://collabbay.com/APIControllers/GetSessionData.php',
          { credentials: 'include' }
        );
        const data = await response.json();

        if (!cancelled) {
          setUsername(data?.Username || null);
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
      } finally {
        if (!cancelled && shouldBlockInitialRender) {
          setLoadingSession(false);
        }
      }
    };

    fetchSessionData();

    return () => {
      cancelled = true;
    };
  }, [shouldBlockInitialRender]);

  // If we chose to block (e.g. loading / or private URLs), show loader until session known
  if (loadingSession && shouldBlockInitialRender) {
    return <FullscreenLoader />;
  }

  return (
    <NavigationContainer linking={linking} fallback={<FullscreenLoader />}>
      <Stack.Navigator initialRouteName="Homepage" screenOptions={{ headerShown: false }}>
        {/* Public screens */}
        <Stack.Screen name="Homepage" component={Homepage} />
        <Stack.Screen name="LogInPage" component={WebLoginRedirect} />
        <Stack.Screen name="SignUpPage" component={SignUpPage} />
        <Stack.Screen name="BrandURLLandingPage" component={BrandURLLandingPage} />
        <Stack.Screen name="CreateSubmissionPage" component={CreateSubmissionPage} />
        <Stack.Screen name="InfluencerInviteLink" component={InfluencerInviteLink} />

        {/* Private screens (REGISTERED ALWAYS, but gated) */}
        <Stack.Screen name="UserRecapPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserRecapPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserCollabDashboard">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserCollabDashboard {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="CollabCampaignDetailsPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <CollabCampaignDetailsPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserGetAssignQRPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserGetAssignQRPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserGetQRSearch">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserGetQRSearch {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="QRCodeScannerPage">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <QRCodeScannerPage {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="MediaUpload">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <MediaUpload {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="CollabCalendar">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <CollabCalendar {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="BrandListings">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <BrandListings {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="PreReleaseListings">
  {(props) => (
    <AuthGate
      username={username}
      loadingSession={loadingSession}
      navigation={props.navigation}
    >
      <PreReleaseListings {...props} />
    </AuthGate>
  )}
</Stack.Screen>


        <Stack.Screen name="ManualVisitRedeem">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <ManualVisitRedeem {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="VenueDirectory">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <VenueDirectory {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UserActivityFeed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UserActivityFeed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="UnclaimedSearch">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <UnclaimedSearch {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="SimilarVenues">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <SimilarVenues {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="MyFeed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <MyFeed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>

        <Stack.Screen name="Feed">
          {(props) => (
            <AuthGate
              username={username}
              loadingSession={loadingSession}
              navigation={props.navigation}
            >
              <Feed {...props} />
            </AuthGate>
          )}
        </Stack.Screen>
      </Stack.Navigator>
    </NavigationContainer>
  );
};

const App = () => {
  // ✅ Router wrapper ONLY on web (prevents crashes if any screens still use react-router hooks/components)
  if (Platform.OS === 'web') {
    return (
      <Router>
        <AppShell />
      </Router>
    );
  }
  return <AppShell />;
};

// Loading spinner styles
const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
});

export default App;
